"use client";

import { useCallback, useEffect, useId, useMemo, useState } from "react";

const AMBIGUOUS = /[0OIl1]/;
const GROUPS = {
  uppercase: "ABCDEFGHIJKLMNOPQRSTUVWXYZ",
  lowercase: "abcdefghijklmnopqrstuvwxyz",
  numbers: "0123456789",
  symbols: "!@#$%^&*()_+-=[]{}<>?/",
} as const;

type GroupKey = keyof typeof GROUPS;

type PasswordEntry = {
  id: string;
  value: string;
};

/**
 * Rejection sampling: draws uniformly from `pool` with no modulo bias.
 * Rejects values that would make the distribution uneven, redrawing as needed.
 */
function randomIndex(poolSize: number): number {
  const maxUint32 = 0xffffffff;
  const limit = maxUint32 - (maxUint32 % poolSize) - 1;
  const buffer = new Uint32Array(1);
  let value: number;
  do {
    crypto.getRandomValues(buffer);
    value = buffer[0];
  } while (value > limit);
  return value % poolSize;
}

function buildPool(selected: GroupKey[], excludeAmbiguous: boolean) {
  return selected.map((key) => {
    const chars = GROUPS[key];
    return excludeAmbiguous ? chars.replace(AMBIGUOUS, "") || chars : chars;
  });
}

/**
 * Generates a password with unbiased randomness, guaranteeing at least one
 * character from each selected group (when the length allows it).
 */
function generatePassword(length: number, groupPools: string[]): string {
  if (groupPools.length === 0 || length <= 0) return "";

  const combined = [...groupPools.join("")];
  const positions = Array.from({ length }, (_, i) => i);

  // Shuffle positions (Fisher-Yates with unbiased randomIndex) so the
  // guaranteed one-per-group characters land in random spots, not the start.
  for (let i = positions.length - 1; i > 0; i -= 1) {
    const j = randomIndex(i + 1);
    [positions[i], positions[j]] = [positions[j], positions[i]];
  }

  const result: string[] = new Array(length);
  const guaranteedCount = Math.min(groupPools.length, length);

  // Place one guaranteed character per selected group first.
  for (let i = 0; i < guaranteedCount; i += 1) {
    const groupChars = [...groupPools[i]];
    result[positions[i]] = groupChars[randomIndex(groupChars.length)];
  }

  // Fill the rest from the combined pool.
  for (let i = guaranteedCount; i < length; i += 1) {
    result[positions[i]] = combined[randomIndex(combined.length)];
  }

  return result.join("");
}

/** Rough entropy estimate in bits, from pool size and length. */
function estimateEntropyBits(length: number, poolSize: number): number {
  if (poolSize <= 1 || length <= 0) return 0;
  return length * Math.log2(poolSize);
}

function strengthFromEntropy(bits: number) {
  if (bits < 40) return { score: 1, label: "Weak", barClass: "bg-rose-500" };
  if (bits < 60) return { score: 2, label: "Fair", barClass: "bg-amber-500" };
  if (bits < 80) return { score: 3, label: "Good", barClass: "bg-sky-500" };
  if (bits < 100) return { score: 4, label: "Strong", barClass: "bg-emerald-500" };
  return { score: 5, label: "Excellent", barClass: "bg-emerald-600" };
}

export default function PasswordGenerator({}: { toolName: string }) {
  const idPrefix = useId();

  const [length, setLength] = useState(16);
  const [includeUppercase, setIncludeUppercase] = useState(true);
  const [includeLowercase, setIncludeLowercase] = useState(true);
  const [includeNumbers, setIncludeNumbers] = useState(true);
  const [includeSymbols, setIncludeSymbols] = useState(true);
  const [excludeAmbiguous, setExcludeAmbiguous] = useState(true);
  const [countInput, setCountInput] = useState("4");
  const [passwords, setPasswords] = useState<PasswordEntry[]>([]);
  const [status, setStatus] = useState("Strong passwords are generated locally in your browser.");
  const [clipboardWarning, setClipboardWarning] = useState("");
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [revealed, setRevealed] = useState(true);

  const selectedGroups = useMemo(() => {
    const keys: GroupKey[] = [];
    if (includeUppercase) keys.push("uppercase");
    if (includeLowercase) keys.push("lowercase");
    if (includeNumbers) keys.push("numbers");
    if (includeSymbols) keys.push("symbols");
    return keys;
  }, [includeUppercase, includeLowercase, includeNumbers, includeSymbols]);

  const groupPools = useMemo(
    () => buildPool(selectedGroups, excludeAmbiguous),
    [selectedGroups, excludeAmbiguous],
  );
  const poolSize = useMemo(() => new Set(groupPools.join("")).size, [groupPools]);
  const entropyBits = estimateEntropyBits(length, poolSize);
  const strength = strengthFromEntropy(entropyBits);
  const count = Math.max(1, Math.min(12, Number.parseInt(countInput, 10) || 1));
  const groupsNotEnoughForLength = selectedGroups.length > length;

  const generate = useCallback(() => {
    if (selectedGroups.length === 0) {
      setPasswords([]);
      setStatus("Choose at least one character group to generate a password.");
      return;
    }

    const next = Array.from({ length: count }, (_, i) => ({
      id: `${Date.now()}-${i}-${Math.random().toString(36).slice(2, 8)}`,
      value: generatePassword(length, groupPools),
    }));

    setPasswords(next);
    setClipboardWarning("");
    setStatus(`Generated ${next.length} password${next.length === 1 ? "" : "s"} locally.`);
  }, [count, length, groupPools, selectedGroups.length]);

  // Regenerate whenever settings change, same as the original behavior.
  useEffect(() => {
    const timeoutId = window.setTimeout(generate, 0);
    return () => window.clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [length, includeUppercase, includeLowercase, includeNumbers, includeSymbols, excludeAmbiguous, count]);

  async function copyPassword(entry: PasswordEntry) {
    try {
      await navigator.clipboard.writeText(entry.value);
      setCopiedId(entry.id);
      setClipboardWarning("");
      window.setTimeout(() => setCopiedId(null), 1400);
    } catch {
      setClipboardWarning("Clipboard access was blocked. Please copy the text manually.");
    }
  }

  async function copyAll() {
    if (passwords.length === 0) return;
    try {
      await navigator.clipboard.writeText(passwords.map((p) => p.value).join("\n"));
      setStatus(`Copied all ${passwords.length} passwords to your clipboard.`);
      setClipboardWarning("");
    } catch {
      setClipboardWarning("Clipboard access was blocked. Please copy the text manually.");
    }
  }

  function regenerateOne(id: string) {
    if (selectedGroups.length === 0) return;
    setPasswords((previous) =>
      previous.map((entry) =>
        entry.id === id ? { ...entry, value: generatePassword(length, groupPools) } : entry,
      ),
    );
  }

  const checkbox = (
    id: string,
    label: string,
    checked: boolean,
    onChange: (checked: boolean) => void,
  ) => (
    <label htmlFor={id} className="flex items-center gap-2 text-sm font-medium text-slate-700">
      <input
        id={id}
        type="checkbox"
        checked={checked}
        onChange={(event) => onChange(event.target.checked)}
      />
      {label}
    </label>
  );

  return (
    <div className="space-y-4">
      <div className="grid gap-4 md:grid-cols-2">
        <label htmlFor={`${idPrefix}-length`} className="text-sm font-medium text-slate-700">
          Length
          <input
            id={`${idPrefix}-length`}
            type="range"
            min="8"
            max="64"
            step="1"
            value={length}
            onChange={(event) => setLength(Number(event.target.value))}
            className="mt-2 w-full"
          />
          <span className="text-xs text-slate-500">{length} characters</span>
        </label>

        <label htmlFor={`${idPrefix}-count`} className="text-sm font-medium text-slate-700">
          Generate at once
          <input
            id={`${idPrefix}-count`}
            type="number"
            min="1"
            max="12"
            inputMode="numeric"
            value={countInput}
            onChange={(event) => setCountInput(event.target.value.replace(/[^\d]/g, ""))}
            onBlur={() => setCountInput(String(count))}
            className="mt-2 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200"
          />
        </label>
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        {checkbox(`${idPrefix}-upper`, "Uppercase letters", includeUppercase, setIncludeUppercase)}
        {checkbox(`${idPrefix}-lower`, "Lowercase letters", includeLowercase, setIncludeLowercase)}
        {checkbox(`${idPrefix}-numbers`, "Numbers", includeNumbers, setIncludeNumbers)}
        {checkbox(`${idPrefix}-symbols`, "Symbols", includeSymbols, setIncludeSymbols)}
      </div>

      {checkbox(`${idPrefix}-ambiguous`, "Exclude ambiguous characters (0, O, 1, I, l)", excludeAmbiguous, setExcludeAmbiguous)}

      {selectedGroups.length === 0 && (
        <p role="alert" className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
          Select at least one character group above.
        </p>
      )}
      {groupsNotEnoughForLength && selectedGroups.length > 0 && (
        <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
          Length is shorter than the number of selected groups, so not every group can appear in every password.
        </p>
      )}

      <div className="flex flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={generate}
          disabled={selectedGroups.length === 0}
          className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-indigo-700 disabled:cursor-not-allowed disabled:bg-slate-300"
        >
          Generate passwords
        </button>
        <button
          type="button"
          onClick={() => setRevealed((current) => !current)}
          className="rounded-lg bg-slate-100 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-200"
        >
          {revealed ? "Hide passwords" : "Show passwords"}
        </button>
        {passwords.length > 1 && (
          <button
            type="button"
            onClick={() => void copyAll()}
            className="rounded-lg bg-slate-100 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-200"
          >
            Copy all
          </button>
        )}
        <span className="rounded-full bg-slate-100 px-3 py-2 text-xs font-medium text-slate-700">
          Client-side only · no upload
        </span>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
        <div className="flex items-center justify-between gap-4">
          <div>
            <p className="text-sm font-semibold text-slate-900">Strength</p>
            <p className="text-xs text-slate-500">
              {strength.label} · ~{Math.round(entropyBits)} bits of entropy
            </p>
          </div>
          <p className="text-sm font-semibold text-slate-900">{strength.score}/5</p>
        </div>
        <div className="mt-3 grid grid-cols-5 gap-2">
          {Array.from({ length: 5 }, (_, index) => (
            <div
              key={index}
              className={`h-2 rounded-full ${index < strength.score ? strength.barClass : "bg-slate-200"}`}
            />
          ))}
        </div>
        <p aria-live="polite" className="mt-3 text-sm text-slate-600">
          {status}
        </p>
        {clipboardWarning && (
          <p role="alert" className="mt-1 text-sm text-rose-600">
            {clipboardWarning}
          </p>
        )}
      </div>

      <div className="space-y-3">
        {passwords.map((entry, index) => (
          <div
            key={entry.id}
            className="flex flex-col gap-3 rounded-xl border border-slate-200 bg-slate-50 p-4 sm:flex-row sm:items-center sm:justify-between"
          >
            <div className="min-w-0 flex-1">
              <p className="truncate font-mono text-sm font-semibold text-slate-900">
                {revealed ? entry.value : "•".repeat(Math.min(entry.value.length, 32))}
              </p>
              <p className="text-xs text-slate-500">Password #{index + 1}</p>
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => regenerateOne(entry.id)}
                aria-label={`Regenerate password ${index + 1}`}
                className="rounded-lg border border-slate-300 px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-white"
              >
                ↻
              </button>
              <button
                type="button"
                onClick={() => void copyPassword(entry)}
                className="rounded-lg bg-slate-900 px-3 py-2 text-xs font-semibold text-white transition-colors hover:bg-slate-800"
              >
                {copiedId === entry.id ? "Copied!" : "Copy"}
              </button>
            </div>
          </div>
        ))}

        {passwords.length === 0 && selectedGroups.length > 0 && (
          <p className="rounded-xl border border-dashed border-slate-200 bg-slate-50 p-4 text-sm text-slate-500">
            Click Generate passwords to create some.
          </p>
        )}
      </div>
    </div>
  );
}